import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { Download, FileLock2, Mail, ShieldCheck } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useTier } from '../../hooks/useTier'
import {
  canSendReportPdf,
  canExportUnredacted,
  canReleaseReport,
  exportReport,
  getReport,
  isReportReadOnly,
  releaseReport,
  triggerReportExportDownload,
  type ReportSummary,
} from '../../lib/services/reports'
import { ReportViewer } from '../../components/reports/ReportViewer'
import { ImprovementPlanSection } from '../../components/reports/ImprovementPlanSection'
import { getAssignments, type Visit } from '../../lib/services/visits'
import { SendReportModal } from './SendReportModal'
import { DeliveryLog } from './DeliveryLog'

export function resolveReportId(routeReportId: string | undefined, pathname: string): string | null {
  if (typeof routeReportId === 'string' && routeReportId.trim()) return routeReportId.trim()
  const match = pathname.match(/\/reports\/([^/?#]+)/)
  return match?.[1] ?? null
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Date not set'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatToken(value: string | null | undefined): string {
  if (!value) return 'Visit'
  return value.replace(/_/g, ' ')
}

export function reportDetailLabels(report: { visit_id: string; version: number }, visits: Visit[]) {
  const visit = visits.find((entry) => entry.id === report.visit_id)
  if (!visit) {
    return {
      title: `Report v${report.version}`,
      subtitle: `Linked visit ending ${report.visit_id.slice(-8)}`,
    }
  }

  return {
    title: `${visit.facility_name} report v${report.version}`,
    subtitle: `${formatToken(visit.visit_type)} survey | ${formatDate(visit.visit_date)}`,
  }
}

export function reportFacilityId(report: { visit_id: string } | null | undefined, visits: Visit[]): string | null {
  if (!report) return null
  return visits.find((entry) => entry.id === report.visit_id)?.facility_id ?? null
}

export function reportDeliveryUnavailableMessage(
  facilityId: string | null,
  report: Pick<ReportSummary, 'status' | 'pdf_generation_status' | 'pdf_storage_path'> | null | undefined,
): string | null {
  if (!facilityId) return 'Delivery is unavailable until the linked facility is loaded.'
  if (!report || canSendReportPdf(report)) return null
  if (report.pdf_generation_status === 'failed') {
    return 'Delivery is unavailable because final PDF generation failed. Release the report again after correcting the draft.'
  }
  return 'Delivery is unavailable until the report is released and the final PDF is generated.'
}

export function ReportDetailsPage() {
  const { reportId: routeReportId } = useParams()
  const location = useLocation()
  const reportId = resolveReportId(routeReportId, location.pathname)
  const { viewerContext } = useAuth()
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [approvalReference, setApprovalReference] = useState('')
  const [isActing, setIsActing] = useState(false)
  const [sendModalOpen, setSendModalOpen] = useState(false)
  const tier = useTier()
  const [exportFormat, setExportFormat] = useState<'standard' | 'cms-2567' | 'branded'>('standard')

  const { data: report, isLoading, error, refetch } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId!),
    enabled: !!reportId,
  })
  const visitsQuery = useQuery({
    queryKey: ['report', reportId, 'visits'],
    queryFn: getAssignments,
    enabled: !!reportId,
  })

  const role = viewerContext?.role || 'Surveyor'
  const readOnly = report ? isReportReadOnly(report.status) : true
  const canRelease = report ? canReleaseReport(role, report.status) : false
  const canUnredacted = canExportUnredacted(role)
  const visits = visitsQuery.data ?? []
  const labels = report ? reportDetailLabels(report, visits) : null
  const facilityId = reportFacilityId(report, visits)
  const reportPdfSendable = report ? canSendReportPdf(report) : false
  const deliveryUnavailableMessage = reportDeliveryUnavailableMessage(facilityId, report)

  if (!reportId) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p className="font-semibold">Unable to load report</p>
        <p className="text-sm">Missing report id in route.</p>
        <Link className="mt-2 inline-block text-sm underline" to="/reports">
          Back to reports
        </Link>
      </div>
    )
  }

  if (isLoading) return <div className="p-10 text-sm text-slate-500">Loading report...</div>
  if (error || !report) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p className="font-semibold">Unable to load report</p>
        <p className="text-sm">{(error as Error | undefined)?.message || 'Report not found'}</p>
        <Link className="mt-2 inline-block text-sm underline" to="/reports">
          Back to reports
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
              Report detail
            </p>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">{labels?.title}</h1>
            <p className="mt-1 text-sm text-slate-600">{labels?.subtitle}</p>
          </div>
          <span className={report.status === 'released' ? 'badge-released' : 'badge-in-progress'}>{report.status}</span>
        </div>
      </header>

      <section className="v11-section p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <FileLock2 size={16} className={readOnly ? 'text-[var(--brand-primary)]' : 'text-slate-400'} />
          {readOnly ? 'Released reports are read-only' : 'Draft report is mutable until release'}
        </div>
        {canRelease ? (
          <button
            className="btn-primary mt-3 text-sm"
            disabled={isActing}
            onClick={async () => {
              setActionError(null)
              setActionSuccess(null)
              setIsActing(true)
              try {
                await releaseReport(report.report_id)
                setActionSuccess('Report released successfully.')
                await refetch()
              } catch (err) {
                setActionError((err as Error).message)
              } finally {
                setIsActing(false)
              }
            }}
          >
            <ShieldCheck size={15} className="mr-1.5 inline-block" />
            Release Report
          </button>
        ) : (
          <p className="mt-3 text-sm text-slate-500">Release action unavailable for your role or report state.</p>
        )}
      </section>

      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Exports</h2>
          <span className="text-xs font-semibold text-slate-500">{canUnredacted ? 'Admin/Reviewer export controls' : 'Redacted export only'}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 p-5">
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as 'standard' | 'cms-2567' | 'branded')}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
          >
            <option value="standard">Standard JSON Export</option>
            <option value="cms-2567">CMS-2567 Export</option>
            {tier === 'consultant' && <option value="branded">Branded PDF</option>}
          </select>
          <button
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            disabled={isActing}
            onClick={async () => {
              setActionError(null)
              setActionSuccess(null)
              setIsActing(true)
              try {
                const result = await exportReport(report.report_id, {
                  unredacted: false,
                  format: exportFormat === 'branded' ? 'cms-2567' : exportFormat,
                  branded: exportFormat === 'branded',
                })
                const filename = triggerReportExportDownload(result, {
                  format: exportFormat === 'branded' ? 'cms-2567' : exportFormat,
                  branded: exportFormat === 'branded',
                })
                setActionSuccess(`${exportFormat === 'cms-2567' ? 'CMS-2567' : exportFormat === 'branded' ? 'Branded PDF' : 'Standard'} export generated: ${filename}`)
              } catch (err) {
                setActionError((err as Error).message)
              } finally {
                setIsActing(false)
              }
            }}
          >
            <Download size={14} className="mr-1.5 inline-block" />
            Export JSON Redacted
          </button>

          <input
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="approval_reference for unredacted export"
            value={approvalReference}
            onChange={(e) => setApprovalReference(e.target.value)}
            disabled={!canUnredacted || isActing}
          />
          <button
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canUnredacted || isActing || !approvalReference.trim()}
            onClick={async () => {
              setActionError(null)
              setActionSuccess(null)
              setIsActing(true)
              try {
                const result = await exportReport(report.report_id, {
                  unredacted: true,
                  approvalReference: approvalReference.trim(),
                  format: exportFormat === 'branded' ? 'cms-2567' : exportFormat,
                  branded: exportFormat === 'branded',
                })
                const filename = triggerReportExportDownload(result, {
                  format: exportFormat === 'branded' ? 'cms-2567' : exportFormat,
                  branded: exportFormat === 'branded',
                })
                setActionSuccess(`Unredacted export generated: ${filename}`)
              } catch (err) {
                setActionError((err as Error).message)
              } finally {
                setIsActing(false)
              }
            }}
          >
            <Download size={14} className="mr-1.5 inline-block" />
            Export JSON Unredacted
          </button>
        </div>
        {!canUnredacted && <p className="px-5 pb-5 text-xs text-slate-500">Unredacted export is restricted to Admin/Reviewer roles.</p>}
        {actionError && <p className="px-5 pb-5 text-sm text-red-600">{actionError}</p>}
        {actionSuccess && <p className="px-5 pb-5 text-sm text-emerald-700">{actionSuccess}</p>}
      </section>

      <section className="v11-section p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Delivery</h2>
            <p className="mt-1 text-sm text-slate-600">Send the final PDF to facility contacts and monitor delivery status.</p>
          </div>
          {facilityId ? (
            <button
              type="button"
              className="btn-primary text-sm disabled:cursor-not-allowed disabled:opacity-60"
              disabled={!reportPdfSendable}
              onClick={() => setSendModalOpen(true)}
            >
              <Mail size={15} className="mr-1.5 inline-block" />
              Send Report PDF
            </button>
          ) : (
            <p className="max-w-xs text-sm text-slate-500">Delivery is unavailable until the linked facility is loaded.</p>
          )}
        </div>
        {deliveryUnavailableMessage && facilityId && (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800">
            {deliveryUnavailableMessage}
          </p>
        )}
        <div className="mt-4">
          <DeliveryLog reportId={report.report_id} />
        </div>
      </section>

      {sendModalOpen && facilityId && reportPdfSendable && (
        <SendReportModal
          reportId={report.report_id}
          facilityId={facilityId}
          onClose={() => setSendModalOpen(false)}
          onSent={(deliveryIds) => {
            setActionSuccess(`Report send queued for ${deliveryIds.length} recipient${deliveryIds.length === 1 ? '' : 's'}.`)
          }}
        />
      )}

      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">View Report</h2>
        </div>
        <div className="p-5">
          <ReportViewer payload={report.report_payload} />
        </div>
      </section>

      <section className="v11-section p-5">
        <ImprovementPlanSection
          visitId={report.visit_id}
          findings={
            report.report_payload && Array.isArray((report.report_payload as Record<string, unknown>).findings)
              ? ((report.report_payload as Record<string, unknown>).findings as { finding_id?: string; domain?: string; severity?: number; observation?: string; standard_ref?: string }[])
              : []
          }
        />
      </section>
    </div>
  )
}
